// src/components/ReportsPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
    Container, Grid, Paper, Typography, Box, Card, CardContent,
    Alert, Skeleton, Select, MenuItem, FormControl, InputLabel,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Chip
} from '@mui/material';
import {
    TrendingUp, AccessTime, Chat, SupportAgent, Schedule
} from '@mui/icons-material';
import axiosInstance from '../api/axiosInstance';

const pageVariants = {
    initial: { opacity: 0, y: 20 },
    in: { opacity: 1, y: 0 },
    out: { opacity: 0, y: -20 }
};

const pageTransition = {
    type: "tween",
    ease: "anticipate",
    duration: 0.6
};

// Simple bar chart component (no external library needed)
const SimpleBarChart = ({ data, dataKey, labelKey, color = '#0ea5e9', height = 200 }) => {
    if (!data || data.length === 0) return <Typography color="text.secondary">لا توجد بيانات</Typography>;

    const maxValue = Math.max(...data.map(d => d[dataKey] || 0));

    return (
        <Box sx={{ height, display: 'flex', alignItems: 'flex-end', gap: 0.5, overflowX: 'auto', pb: 3 }}>
            {data.map((item, index) => {
                const value = item[dataKey] || 0;
                const barHeight = maxValue > 0 ? (value / maxValue) * (height - 30) : 0;
                const label = item[labelKey] || '';

                return (
                    <Box key={index} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 30 }}>
                        <Typography variant="caption" sx={{ mb: 0.5 }}>{value}</Typography>
                        <Box
                            sx={{
                                width: 24,
                                height: barHeight,
                                bgcolor: color,
                                borderRadius: '4px 4px 0 0',
                                transition: 'height 0.3s ease'
                            }}
                        />
                        <Typography variant="caption" sx={{ mt: 0.5, fontSize: 10, transform: 'rotate(-45deg)', transformOrigin: 'top left' }}>
                            {typeof label === 'string' ? label.slice(-5) : label}
                        </Typography>
                    </Box>
                );
            })}
        </Box>
    );
};

// Stat card component
const StatCard = ({ icon, title, value, subtitle, color = 'primary.main' }) => (
    <Card sx={{ height: '100%' }}>
        <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: `${color}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    {React.cloneElement(icon, { sx: { color, fontSize: 28 } })}
                </Box>
                <Box>
                    <Typography variant="h4" fontWeight="700">{value}</Typography>
                    <Typography variant="body2" color="text.secondary">{title}</Typography>
                    {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
                </Box>
            </Box>
        </CardContent>
    </Card>
);

export default function ReportsPage() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [days, setDays] = useState(30);
    const [reportData, setReportData] = useState(null);

    const fetchReports = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const response = await axiosInstance.get(`/chatbot/reports/?days=${days}`);
            setReportData(response.data);
        } catch (err) {
            console.error('Error fetching reports:', err);
            setError('فشل في تحميل التقارير');
        } finally {
            setLoading(false);
        }
    }, [days]);

    useEffect(() => {
        fetchReports();
    }, [fetchReports]);

    const formatResponseTime = (seconds) => {
        if (seconds < 60) return `${Math.round(seconds)} ثانية`;
        if (seconds < 3600) return `${Math.round(seconds / 60)} دقيقة`;
        return `${Math.round(seconds / 3600)} ساعة`;
    };

    const getStatusLabel = (status) => {
        const labels = {
            'active_bot': 'مع البوت',
            'active_human': 'مع الدعم',
            'closed': 'مغلق',
            'pending_department_choice': 'بانتظار الاختيار'
        };
        return labels[status] || status;
    };

    if (loading) {
        return (
            <Container maxWidth="lg">
                <Box sx={{ mb: 4 }}>
                    <Skeleton variant="text" width="40%" height={40} />
                    <Skeleton variant="text" width="60%" height={24} />
                </Box>
                <Grid container spacing={3}>
                    {[1, 2, 3, 4].map((item) => (
                        <Grid key={item} item xs={12} sm={6} md={3}>
                            <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
                        </Grid>
                    ))}
                </Grid>
            </Container>
        );
    }

    const { summary, conversations_per_day, messages_per_day, status_distribution, department_stats, peak_hours } = reportData || {};

    return (
        <motion.div
            initial="initial"
            animate="in"
            exit="out"
            variants={pageVariants}
            transition={pageTransition}
        >
            <Container maxWidth="lg">
                <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <Box>
                        <Typography variant="h4" fontWeight="700" gutterBottom>
                            التقارير والإحصائيات
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            تحليلات مفصلة لأداء المحادثات والردود
                        </Typography>
                    </Box>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>الفترة الزمنية</InputLabel>
                        <Select
                            value={days}
                            label="الفترة الزمنية"
                            onChange={(e) => setDays(e.target.value)}
                        >
                            <MenuItem value={7}>آخر 7 أيام</MenuItem>
                            <MenuItem value={30}>آخر 30 يوم</MenuItem>
                            <MenuItem value={90}>آخر 3 أشهر</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                {/* Summary Cards */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <StatCard
                            icon={<Chat />}
                            title="إجمالي المحادثات"
                            value={summary?.total_conversations || 0}
                            color="#0ea5e9"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <StatCard
                            icon={<TrendingUp />}
                            title="إجمالي الرسائل"
                            value={summary?.total_messages || 0}
                            subtitle={`${summary?.total_user_messages || 0} مستخدم / ${summary?.total_bot_messages || 0} بوت`}
                            color="#10b981"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <StatCard
                            icon={<AccessTime />}
                            title="متوسط وقت الرد"
                            value={formatResponseTime(summary?.avg_response_time_seconds || 0)}
                            color="#f59e0b"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <StatCard
                            icon={<SupportAgent />}
                            title="معدل التحويل للدعم"
                            value={`${summary?.transfer_rate_percent || 0}%`}
                            color="#ef4444"
                        />
                    </Grid>
                </Grid>

                {/* Charts Row */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3, height: '100%' }}>
                            <Typography variant="h6" fontWeight="600" gutterBottom>
                                المحادثات يومياً
                            </Typography>
                            <SimpleBarChart
                                data={conversations_per_day}
                                dataKey="count"
                                labelKey="date"
                                color="#0ea5e9"
                            />
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3, height: '100%' }}>
                            <Typography variant="h6" fontWeight="600" gutterBottom>
                                الرسائل يومياً
                            </Typography>
                            <SimpleBarChart
                                data={messages_per_day}
                                dataKey="count"
                                labelKey="date"
                                color="#10b981"
                            />
                        </Paper>
                    </Grid>
                </Grid>

                {/* Status and Department Stats */}
                <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 3, height: '100%' }}>
                            <Typography variant="h6" fontWeight="600" gutterBottom>
                                توزيع الحالات
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                                {status_distribution?.map((item, idx) => (
                                    <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Chip label={getStatusLabel(item.status)} size="small" variant="outlined" />
                                        <Typography fontWeight="600">{item.count}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 3, height: '100%' }}>
                            <Typography variant="h6" fontWeight="600" gutterBottom>
                                أداء الأقسام
                            </Typography>
                            {department_stats?.length > 0 ? (
                                <TableContainer sx={{ mt: 2 }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>القسم</TableCell>
                                                <TableCell align="center">التحويلات</TableCell>
                                                <TableCell align="center">ناجح</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {department_stats.map((dept, idx) => (
                                                <TableRow key={idx}>
                                                    <TableCell>{dept.department__name}</TableCell>
                                                    <TableCell align="center">{dept.total_transfers}</TableCell>
                                                    <TableCell align="center">
                                                        <Chip
                                                            label={dept.successful}
                                                            size="small"
                                                            color="success"
                                                            variant="outlined"
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) : (
                                <Typography color="text.secondary" sx={{ mt: 2 }}>لا توجد تحويلات</Typography>
                            )}
                        </Paper>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 3, height: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <Schedule color="primary" />
                                <Typography variant="h6" fontWeight="600">
                                    أوقات الذروة
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {peak_hours?.map((item, idx) => (
                                    <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography>
                                            {`${String(Math.floor(item.hour)).padStart(2, '0')}:00`}
                                        </Typography>
                                        <Chip label={`${item.count} رسالة`} size="small" color="primary" variant="outlined" />
                                    </Box>
                                ))}
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </Container>
        </motion.div>
    );
}
